import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';
import actionLabels from '../../config/actionLabels';

const Positions = () => {
    const [positions, setPositions] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
    const [editingPosition, setEditingPosition] = useState(null);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedModules, setSelectedModules] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [moduleActions, setModuleActions] = useState({});
    const { register, handleSubmit, reset, setValue } = useForm();
    const { logout, hasPermission, modules, getRole, getAllRoles } = useUser();

	const canEdit = hasPermission("positions", "edit");
	const canDelete = hasPermission("positions", "delete");
	const canView = hasPermission("positions", "view");
	const canCreate = hasPermission("positions", "create");

	

    const fetchPositions = async () => {
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/positions`,
                {},
                logout
            );
            const data = await res.json();
            setPositions(data);
        } catch (error) {
            console.error('Ошибка при загрузке должностей:', error);
        }
    };

    useEffect(() => {
        fetchPositions();
    }, []);

	if (!canView) {
		return <div className="text-center text-2xl text-red-500">У вас нет доступа к просмотру должностей</div>;
	}
	
    const handleModuleSelect = (moduleKey, checked) => {
        if (checked) {
            setSelectedModules([...selectedModules, moduleKey]);
            setModuleActions(prev => ({
                ...prev,
                [moduleKey]: ['view'] // По умолчанию добавляем право на просмотр
            }));
        } else {
            setSelectedModules(selectedModules.filter(m => m !== moduleKey));
            setModuleActions(prev => {
                const newActions = { ...prev };
                delete newActions[moduleKey];
                return newActions;
            });
        }
    };

    const handleActionToggle = (moduleKey, action) => {
        setModuleActions(prev => {
            const currentActions = prev[moduleKey] || [];
            const newActions = currentActions.includes(action)
                ? currentActions.filter(a => a !== action)
                : [...currentActions, action];
            
            return {
                ...prev,
                [moduleKey]: newActions
            };
        });
    };

    const handleEdit = (position) => {
        setEditingPosition(position);
        setValue('name', position.name);
        setValue('description', position.description);
        setSelectedModules(position.modulePermissions.map(p => p.module));
        setSelectedRole(position.role);
        setModuleActions(
            position.modulePermissions.reduce((acc, perm) => ({
                ...acc,
                [perm.module]: perm.actions
            }), {})
        );
        setIsModalVisible(true);
    };

    const handleAdd = () => {
        setEditingPosition(null);
        reset();
        setSelectedModules([]);
        setSelectedRole('');
        setModuleActions({});
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту должность?')) return;
        
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/positions/${id}`,
                {
                    method: 'DELETE'
                },
                logout
            );
            if (res.ok) {
                fetchPositions();
            } else {
                throw new Error('Ошибка при удалении');
            }
        } catch (error) {
            console.error('Ошибка при удалении должности:', error);
        }
    };

    const handleDetails = (position) => {
        setSelectedPosition(position);
        setIsDetailsModalVisible(true);
    };

    const handleRoleSelect = (roleKey) => {
        setSelectedRole(roleKey);
    };

    const onSubmit = async (data) => {
        try {
            const positionData = {
                name: data.name,
                description: data.description,
                modulePermissions: selectedModules.map(moduleKey => ({
                    module: moduleKey,
                    actions: moduleActions[moduleKey] || []
                })),
                role: selectedRole
            };

            const url = editingPosition
                ? `${process.env.REACT_APP_API_URL}/positions/${editingPosition._id}`
                : `${process.env.REACT_APP_API_URL}/positions`;

            const res = await fetchWithAuth(
                url,
                {
                    method: editingPosition ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(positionData)
                },
                logout
            );

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Ошибка при сохранении должности');
            }

            await fetchPositions();
            setIsModalVisible(false);
            reset();
            setSelectedModules([]);
            setSelectedRole('');
            setModuleActions({});
        } catch (error) {
            console.error('Ошибка при сохранении должности:', error);
            alert(error.message);
        }
    };

	if (!modules || Object.keys(modules).length === 0) {
		return <div className="text-center text-gray-400">Загрузка конфигурации модулей...</div>;
	}
	
    return (
        <div >
            {canCreate && <div className="flex justify-between items-center mb-6">
                <Button
                    onClick={handleAdd}
                    variant="primary"
                >
                    Добавить новую должность
                </Button>
            </div>}

            <table className="w-full border-collapse">
                <thead>
                    <tr className="bg-[#2D2D30]">
                        <th className="p-3 text-left">Название</th>
                        <th className="p-3 text-left">Описание</th>
                        <th className="p-3 text-left">Доступные модули</th>
                        <th className="p-3 text-left">Роль</th>
                        <th className="p-3 text-left">Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {positions.map(position => (
                        <tr key={position._id} className="border-t border-[#3E3E42]">
                            <td className="p-3">{position.name}</td>
                            <td className="p-3">{position.description}</td>
                            <td className="p-3">
                                <Button
                                    onClick={() => handleDetails(position)}
                                    variant="edit"
                                    className="mr-2"
                                >
                                    Подробнее
                                </Button>
                            </td>
                            <td className="p-3">
                                <span className="px-2 py-1 bg-[#3E3E42] rounded-full text-sm">
                                    {getRole(position.role)?.name}
                                </span>
                            </td>
                            <td className="p-3">
                                {canEdit && <Button
                                    onClick={() => handleEdit(position)}
                                    variant="edit"
                                    className="mr-2"
                                >
                                    Редактировать
                                </Button>}
                                {canDelete && <Button
                                    onClick={() => handleDelete(position._id)}
                                    variant="danger"
                                >
                                    Удалить
                                </Button>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {isDetailsModalVisible && selectedPosition && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-[#252526] p-6 rounded-lg w-[600px] max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">
                                Права доступа: {selectedPosition.name}
                            </h3>
                            <Button
                                onClick={() => setIsDetailsModalVisible(false)}
                                variant="cancel"
                            >
                                Закрыть
                            </Button>
                        </div>
                        
                        <div className="space-y-4">
                            {selectedPosition.modulePermissions.map(permission => {
                                const module = modules[permission.module];
                                return (
                                    <div key={permission.module} className="bg-[#2D2D30] p-4 rounded-lg">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className="font-medium text-lg">{module.name}</div>
                                                <div className="text-sm text-gray-500 mt-1">{module.description}</div>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {permission.actions.map(action => (
                                                    <span 
                                                        key={action} 
                                                        className="px-3 py-1 bg-[#3E3E42] rounded-full text-sm font-medium"
                                                    >
                                                       {actionLabels[action] || action}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {isModalVisible && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ">
                    <div className="bg-[#252526] p-6 rounded-lg w-full max-w-7xl h-full overflow-y-auto">
                        <h3 className="text-xl font-semibold mb-4">
                            {editingPosition ? 'Редактировать должность' : 'Добавить должность'}
                        </h3>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Название</label>
                                <input
                                    {...register('name', { required: true })}
                                    className="w-full px-3 py-2 bg-[#2D2D30] border border-[#3E3E42] rounded"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Описание</label>
                                <input
                                    {...register('description')}
                                    className="w-full px-3 py-2 bg-[#2D2D30] border border-[#3E3E42] rounded"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Доступные модули</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(modules).map(([key, module]) => (
                                        <div key={key} className="bg-[#2D2D30] p-3 rounded">
                                            <label className="flex items-start">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedModules.includes(key)}
                                                    onChange={(e) => handleModuleSelect(key, e.target.checked)}
                                                    className="mt-1 mr-2"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium">{module.name}</div>
                                                    <div className="text-sm text-gray-500">{module.description}</div>
                                                    {selectedModules.includes(key) && (
                                                        <div className="mt-2">
                                                            <div className="text-sm font-medium mb-1">Доступные действия:</div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {module.actions.map(action => (
                                                                    <label key={action} className="flex items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={moduleActions[key]?.includes(action)}
                                                                            onChange={() => handleActionToggle(key, action)}
                                                                            className="mr-1"
                                                                        />
                                                                        <span className="text-sm">{actionLabels[action] || action}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Роль</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(getAllRoles()).map(([key, role]) => (
                                        <div key={key} className="bg-[#2D2D30] p-3 rounded">
                                            <label className="flex items-start">
                                                <input
                                                    type="radio"
                                                    name="role"
                                                    checked={selectedRole === key}
                                                    onChange={() => handleRoleSelect(key)}
                                                    className="mt-1 mr-2"
                                                />
                                                <div>
                                                    <div className="font-medium">{role.name}</div>
                                                    <div className="text-sm text-gray-500">{role.description}</div>
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    onClick={() => setIsModalVisible(false)}
                                    variant="cancel"
                                >
                                    Отмена
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                >
                                    Сохранить
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Positions; 